import React, { useEffect } from 'react';
import { ApplicationControls } from '../ApplicationControls';
import { CollapsibleSidebar } from './CollapsibleSidebar';
import { ApplicationSection } from '../ApplicationSection';
import { AutosaveIndicator } from './AutosaveIndicator';
import { ApplicationBreadcrumb } from './ApplicationBreadcrumb';
import type { Application } from '@/types/database.types';
import { SECTIONS } from '@/constants/application';
import { useApplicationProgress } from '@/hooks/use-application-progress';

interface ApplicationContentProps {
  application: Application;
  currentSection: number;
  setCurrentSection: (section: number) => void;
  lastSaved: Date | null;
  progress: number;
  onSave: (data: Partial<Application>) => Promise<void>;
  isLoading: boolean;
}

export const ApplicationContent: React.FC<ApplicationContentProps> = ({
  application,
  currentSection,
  setCurrentSection,
  lastSaved,
  progress,
  onSave,
  isLoading,
}) => {
  // Store form data changes temporarily without saving to database
  const [formData, setFormData] = React.useState<Partial<Application>>({});

  // Calculate application progress directly here to ensure it's up to date
  const { calculateProgress, getCompletedSections } = useApplicationProgress(application);
  const progressValue = calculateProgress();
  const completedSections = getCompletedSections();

  // Log the progress to help with debugging
  React.useEffect(() => {
    console.log('Current progress:', progressValue, '%');
    console.log('Completed sections:', completedSections);
    // Log application ID for debugging
    console.log('Current application ID:', application?.id);
  }, [progressValue, completedSections, application?.id]);

  // Ensure current section is valid
  useEffect(() => {
    if (currentSection >= SECTIONS.length) {
      setCurrentSection(0);
    }
  }, [currentSection, setCurrentSection]);

  // Handle form data changes without saving to database
  const handleFormDataChange = (data: Partial<Application>) => {
    setFormData(prevData => ({
      ...prevData,
      ...data
    }));
  };

  // Save all accumulated form changes to database
  const handleSaveChanges = async () => {
    if (Object.keys(formData).length > 0) {
      await onSave(formData);
      // Clear the form data after saving
      setFormData({});
    } else {
      // If no changes, just return a resolved promise
      return Promise.resolve();
    }
  };

  // Save and navigate to previous section
  const handleSaveAndPrevious = async () => {
    await handleSaveChanges();
    setCurrentSection(Math.max(0, currentSection - 1));
  };

  // Create a Promise-returning wrapper for ApplicationSection
  const handleSectionSave = async (data: Partial<Application>): Promise<void> => {
    // Just update local form data, don't save to database yet
    handleFormDataChange(data);
    // Return a resolved promise to satisfy the type requirement
    return Promise.resolve();
  };

  // Create an enhanced ApplicationSection component with navigation
  const renderApplicationSection = () => {
    if (currentSection === 8) { // Review & Submit section
      return (
        <ApplicationSection 
          currentSection={currentSection}
          data={{...application, ...formData}}
          onSave={handleSectionSave}
          onSectionChange={setCurrentSection}
        />
      );
    }
    
    return (
      <ApplicationSection 
        currentSection={currentSection}
        data={{...application, ...formData}}
        onSave={handleSectionSave}
      />
    );
  };

  return (
    <div className="flex flex-col md:flex-row min-h-[calc(100vh-64px)]">
      <CollapsibleSidebar
        currentSection={currentSection} 
        onSectionChange={setCurrentSection}
        data={application}
        className="bg-background border-r animate-fadeIn"
      />

      <main className="flex-1 p-6 pt-6 bg-gradient-subtle overflow-y-auto">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-col gap-6 staggered-children">
            {/* Progress bar - now using the directly calculated value */}
            <div className="glass-card p-4 border-primary/10 shadow-elevation-1">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-sm font-medium text-slate-700">Overall Progress</h4>
                <span className="text-sm font-medium text-primary">{progressValue}%</span>
              </div>
              <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-primary/80 to-primary transition-all duration-500 ease-in-out relative overflow-hidden"
                  style={{ width: `${progressValue}%` }}
                >
                  <div className="absolute inset-0 bg-gradient-shimmer bg-[length:100%_100%] animate-shimmer"></div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <ApplicationBreadcrumb currentSection={currentSection} />
              <AutosaveIndicator lastSaved={lastSaved} />
            </div>

            {/* Application section content */}
            <div className="glass-card border border-white/30 shadow-elevation-2 rounded-xl overflow-hidden">
              {renderApplicationSection()}
            </div>

            <ApplicationControls
              currentSection={currentSection}
              totalSections={SECTIONS.length}
              onPrevious={() => setCurrentSection(Math.max(0, currentSection - 1))}
              onNext={() => setCurrentSection(Math.min(SECTIONS.length - 1, currentSection + 1))}
              onSave={handleSaveChanges}
              onSaveAndPrevious={handleSaveAndPrevious}
              isLoading={isLoading}
            />
          </div>
        </div>
      </main>
    </div>
  );
};
